<template>
  <div class="konsensus-kaedah">
    <!-- Empty state: no item selected -->
    <v-card v-if="!kod" outlined class="mb-4">
      <v-card-text class="text-center py-10 grey--text">
        <v-icon size="48" color="grey lighten-1">mdi-cursor-default-click-outline</v-icon>
        <div class="mt-3">Pilih satu item dari senarai di atas untuk analisis konsensus pelbagai kaedah.</div>
      </v-card-text>
    </v-card>

    <!-- Loading -->
    <v-card v-else-if="loading" outlined class="mb-4">
      <v-card-text class="text-center py-10">
        <v-progress-circular indeterminate color="teal" size="48" width="4"></v-progress-circular>
        <div class="mt-3 caption text--secondary">Mengira anomali (4 kaedah)…</div>
      </v-card-text>
    </v-card>

    <template v-else-if="data">
      <!-- Header summary -->
      <v-card outlined class="mb-4 consensus-head">
        <v-card-text class="d-flex align-center flex-wrap py-3">
          <v-avatar size="42" color="indigo lighten-4" class="mr-3">
            <v-icon color="indigo darken-2">mdi-shield-search</v-icon>
          </v-avatar>
          <div class="mr-6" style="min-width:0">
            <div class="overline indigo--text text--darken-2" style="line-height:1;font-size: var(--fs-3xs) !important">Konsensus {{ methodCount }}-Kaedah</div>
            <div class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-4 text-truncate">{{ data.nama || kod }}</div>
            <div class="caption text--secondary">{{ kod }} · {{ fmtN(data.total) }} rekod · kiraan &amp; jadual penuh<span v-if="data.capped"> (graf guna sampel {{ fmtN(data.returned) }} rekod untuk prestasi; anomali sentiasa disertakan)</span></div>
          </div>
          <v-spacer></v-spacer>
          <div class="text-right">
            <div class="text-h4 font-weight-bold indigo--text text--darken-2">{{ fmtN(anomalyTotal) }}</div>
            <div class="overline text--secondary" style="font-size: var(--fs-3xs)">Anomali Dikesan</div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Confidence buckets -->
      <v-row class="mb-1" dense>
        <v-col v-for="b in bucketCards" :key="b.key" cols="6" sm="4" md="2" class="conf-col">
          <v-card
            outlined class="pa-3 conf-card"
            :class="{ active: confFilter === b.key }"
            :style="{ borderLeft: '4px solid ' + b.color }"
            @click="toggleConf(b.key)"
          >
            <div class="overline text--secondary" style="font-size: var(--fs-3xs);letter-spacing:.08em">{{ b.label }}</div>
            <div class="text-h5 font-weight-bold" :style="{ color: b.color }">{{ fmtN(b.value) }}</div>
            <div class="caption text--secondary" style="font-size: var(--fs-3xs)">{{ b.desc }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Method status -->
      <v-card outlined class="mb-4">
        <v-card-title class="py-2 subtitle-2 blue-grey--text text--darken-2">
          <v-icon small left>mdi-function-variant</v-icon>Kaedah Pengesanan Aktif
          <v-spacer></v-spacer>
          <v-btn x-small outlined color="blue-grey darken-1" class="text-none" @click="methodDialog = true">
            <v-icon x-small left>mdi-information-outline</v-icon>Cara pengiraan
          </v-btn>
        </v-card-title>
        <v-card-text class="py-2">
          <v-row dense>
            <v-col v-for="m in data.methods" :key="m.code" cols="12" sm="4">
              <div class="method-card d-flex align-center">
                <div class="method-code mr-3">{{ m.code }}</div>
                <div style="flex:1;min-width:0">
                  <div class="subtitle-2 blue-grey--text text--darken-3">{{ m.name }}</div>
                  <div class="caption text--secondary" style="line-height:1.3">{{ m.desc }}</div>
                </div>
                <div class="text-h6 font-weight-bold red--text">{{ fmtN(m.count) }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Scatter -->
      <v-card outlined class="mb-4">
        <v-card-title class="py-2 subtitle-2">
          <span class="indigo--text mr-2">&#9679;</span>
          Taburan Bivariat — {{ yLabel }} vs Kuantiti (skala log)
          <span class="caption grey--text ml-3">warna = tahap konsensus</span>
        </v-card-title>
        <v-card-text class="chart-container">
          <canvas v-show="scatterPoints.length" ref="scatter"></canvas>
          <div v-if="!scatterPoints.length" class="d-flex flex-column align-center justify-center grey--text" style="height:100%">
            <v-icon size="40" color="grey lighten-1">mdi-chart-scatter-plot</v-icon>
            <div class="mt-2 caption">Tiada titik untuk diplot (perlu Kuantiti &gt; 0 dan {{ yLabel }} &gt; 0).</div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Records table -->
      <v-card outlined class="mb-4">
        <v-card-title class="py-3 d-flex align-center">
          <v-icon small class="mr-2">mdi-table</v-icon>
          <span class="subtitle-1 font-weight-medium">Rekod Anomali — Penapis Konsensus</span>
          <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ fmtN(tableTotal) }} rekod</v-chip>
          <v-spacer></v-spacer>
          <download-excel :fetch="exportFetch" type="csv" :name="exportName">
            <v-btn small color="green darken-2" dark class="text-none">
              <v-icon small left>mdi-file-excel</v-icon>CSV
            </v-btn>
          </download-excel>
        </v-card-title>

        <v-card-text class="py-2 grey lighten-5">
          <v-row dense align="center">
            <v-col cols="6" sm="3">
              <v-select
                v-model="confFilter" :items="confFilterItems"
                label="Tahap Konsensus" outlined dense hide-details clearable
                @change="onFilterChange"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3">
              <v-select
                v-model="methodFilter" :items="methodFilterItems"
                label="Ditanda Kaedah" outlined dense hide-details clearable
                @change="onFilterChange"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3">
              <v-select
                v-model="dirFilter" :items="dirFilterItems"
                label="Arah" outlined dense hide-details clearable
                @change="onFilterChange"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="search" label="Cari negeri / HID"
                prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                @input="onSearchInput" @click:clear="onFilterChange"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Legend / panduan membaca jadual (boleh buka-tutup) -->
        <v-expansion-panels flat tile class="legend-panel">
          <v-expansion-panel>
            <v-expansion-panel-header class="py-2 px-4 caption font-weight-medium blue-grey--text text--darken-2">
              <div>
                <v-icon small left color="indigo darken-1">mdi-help-circle-outline</v-icon>
                Panduan membaca jadual — apa maksud setiap lajur &amp; bila ia bertukar warna
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="legend-grid">
                <div v-for="lg in columnLegend" :key="lg.key" class="legend-item">
                  <div class="legend-head">
                    <span class="legend-name">{{ lg.name }}</span>
                    <span v-if="lg.sample" class="legend-sample" v-html="lg.sample"></span>
                  </div>
                  <div class="legend-desc">{{ lg.desc }}</div>
                  <div v-if="lg.on" class="legend-on">
                    <v-icon x-small color="red darken-1">mdi-alert-circle</v-icon>
                    <span>{{ lg.on }}</span>
                  </div>
                </div>
              </div>

              <!-- ── Worked visual examples (Z-Sisa & Mahalanobis), using THIS item ── -->
              <v-divider class="my-3"></v-divider>
              <div class="caption font-weight-medium blue-grey--text text--darken-2 mb-2">
                Contoh visual berdasarkan item ini — kenapa nilai bertukar <span class="red--text font-weight-bold">merah</span>:
              </div>
              <div class="example-grid">
                <!-- Z-Sisa number line -->
                <div class="example-box">
                  <div class="ex-title">Z-Sisa — garis nombor</div>
                  <div class="ex-sub">Zon merah = |Z| &gt; {{ zScaleExample.thresh }} (terlalu jauh dari jangkaan). Hampir 0 = normal.</div>
                  <div class="numline">
                    <!-- red zones beyond ±threshold -->
                    <div class="nl-zone" :style="{ left: '0%', width: zScaleExample.lowerPct + '%' }"></div>
                    <div class="nl-zone" :style="{ left: zScaleExample.upperPct + '%', right: '0' }"></div>
                    <!-- threshold ticks -->
                    <div class="nl-tick" :style="{ left: zScaleExample.lowerPct + '%' }"><span>−{{ zScaleExample.thresh }}</span></div>
                    <div class="nl-tick center"><span>0</span></div>
                    <div class="nl-tick" :style="{ left: zScaleExample.upperPct + '%' }"><span>+{{ zScaleExample.thresh }}</span></div>
                    <!-- this item's marker -->
                    <div v-if="zScaleExample.has" class="nl-marker"
                      :class="{ flagged: zScaleExample.flagged }"
                      :style="{ left: zScaleExample.markerPct + '%' }"
                      :title="'Z = ' + zScaleExample.markerZ.toFixed(2)"></div>
                  </div>
                  <div v-if="zScaleExample.has" class="ex-read">
                    Contoh terkuat: <b>{{ zScaleExample.negeri }}</b>
                    <v-chip v-if="zScaleExample.hid" x-small color="teal lighten-4" text-color="teal darken-2" class="mx-1">HID {{ zScaleExample.hid }}</v-chip>
                    — harga {{ fmt(zScaleExample.rmNegeri) }}
                    <template v-if="zScaleExample.expected != null">vs jangkaan {{ fmt(zScaleExample.expected) }} </template>→
                    <b :class="zScaleExample.flagged ? 'red--text' : 'green--text text--darken-1'">Z = {{ zScaleExample.markerZ.toFixed(2) }}</b>
                    <template v-if="zScaleExample.flagged"> (dalam zon merah → ditanda)</template>
                    <template v-else> (dalam julat normal)</template>
                  </div>
                  <div v-else class="ex-read grey--text">Tiada data regresi untuk item ini.</div>
                </div>

                <!-- Mahalanobis distance scale -->
                <div class="example-box">
                  <div class="ex-title">Mahalanobis — skala jarak</div>
                  <div class="ex-sub">Zon merah = jarak melebihi ambang {{ mahalScaleExample.thresh.toFixed(2) }}. Makin jauh = makin terpencil.</div>
                  <div class="numline">
                    <div class="nl-zone" :style="{ left: mahalScaleExample.threshPct + '%', right: '0' }"></div>
                    <div class="nl-tick" :style="{ left: '0%' }"><span>0</span></div>
                    <div class="nl-tick" :style="{ left: mahalScaleExample.threshPct + '%' }"><span>{{ mahalScaleExample.thresh.toFixed(1) }}</span></div>
                    <div v-if="mahalScaleExample.has" class="nl-marker"
                      :class="{ flagged: mahalScaleExample.flagged }"
                      :style="{ left: mahalScaleExample.markerPct + '%' }"
                      :title="'Jarak = ' + mahalScaleExample.markerD.toFixed(2)"></div>
                  </div>
                  <div v-if="mahalScaleExample.has" class="ex-read">
                    Contoh terjauh: <b>{{ mahalScaleExample.negeri }}</b>
                    <v-chip v-if="mahalScaleExample.hid" x-small color="teal lighten-4" text-color="teal darken-2" class="mx-1">HID {{ mahalScaleExample.hid }}</v-chip>
                    — {{ fmt(mahalScaleExample.rmNegeri) }} / {{ fmtN(mahalScaleExample.kuantiti) }} unit →
                    <b :class="mahalScaleExample.flagged ? 'red--text' : 'green--text text--darken-1'">jarak = {{ mahalScaleExample.markerD.toFixed(2) }}</b>
                    <template v-if="mahalScaleExample.flagged"> (melebihi ambang → ditanda)</template>
                    <template v-else> (dalam kelompok biasa)</template>
                  </div>
                  <div v-else class="ex-read grey--text">Tiada data Mahalanobis untuk item ini.</div>
                </div>
              </div>

              <!-- Method-dot key for the "Kaedah" column -->
              <v-divider class="my-3"></v-divider>
              <div class="caption font-weight-medium blue-grey--text text--darken-2 mb-2">
                Lajur <b>Kaedah</b> — 4 kotak (setiap kaedah). Kotak <b>merah</b> = kaedah itu menanda rekod; <b>kelabu</b> = tidak.
              </div>
              <div class="dot-key">
                <div v-for="dk in methodKey" :key="dk.code" class="dot-key-item">
                  <span class="method-dot on">{{ dk.short }}</span>
                  <div>
                    <div class="dk-name">{{ dk.code }} — {{ dk.name }}</div>
                    <div class="dk-desc">{{ dk.desc }}</div>
                  </div>
                </div>
              </div>

              <!-- Confidence level key -->
              <v-divider class="my-3"></v-divider>
              <div class="caption font-weight-medium blue-grey--text text--darken-2 mb-2">
                Lajur <b>Tahap</b> / <b>Skor</b> — berapa banyak kaedah bersetuju rekod itu ganjil:
              </div>
              <div class="conf-key">
                <v-chip v-for="b in bucketCards" :key="b.key" x-small
                  :color="confBg(b.key)" :text-color="confText(b.key)" class="mr-1 mb-1">
                  {{ b.label }} · {{ b.desc }}
                </v-chip>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-data-table
          :headers="headers"
          :items="tableItems"
          :options.sync="tableOptions"
          :server-items-length="tableTotal"
          :loading="tableLoading"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
          dense
        >
          <!-- Header tooltips: hover the ⓘ for a plain-language explanation -->
          <template v-for="h in helpHeaders" v-slot:[`header.${h.value}`]="{ header }">
            <span :key="h.value">
              {{ header.text }}
              <v-tooltip top max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small class="ml-1 legend-help-icon" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>{{ h.help }}</span>
              </v-tooltip>
            </span>
          </template>

          <template v-slot:item.confidence="{ item }">
            <v-chip x-small :color="confBg(item.confidence)" :text-color="confText(item.confidence)">
              {{ item.confidence }}
            </v-chip>
          </template>
          <template v-slot:item.consensus="{ item }">
            <span class="font-weight-bold">{{ item.consensus }}/{{ methodCount }}</span>
          </template>
          <template v-slot:item.negeriName="{ item }">
            {{ item.negeriName }} <span class="grey--text caption">({{ item.negeri }})</span>
          </template>
          <template v-slot:item.rmNegeri="{ item }">
            <span :class="{ 'red--text font-weight-bold': item.upAnomaly }">{{ fmt(item.rmNegeri) }}</span>
          </template>
          <template v-slot:item.rmHQ="{ item }"><span class="grey--text">{{ fmt(item.rmHQ) }}</span></template>
          <template v-slot:item.kuantiti="{ item }">{{ fmtN(item.kuantiti) }}</template>
          <template v-slot:item.unitPrice="{ item }">
            <span :class="{ 'red--text font-weight-bold': item.upAnomaly }">{{ fmtU(item.unitPrice) }}</span>
          </template>
          <template v-slot:item.expectedPrice="{ item }">
            <span v-if="item.expectedPrice != null" class="grey--text">{{ fmt(item.expectedPrice) }}</span>
            <v-tooltip v-else-if="item.expectedReliable === false" top max-width="260">
              <template v-slot:activator="{ on, attrs }">
                <span class="grey--text" v-bind="attrs" v-on="on">—<v-icon x-small class="ml-1" color="amber darken-2">mdi-alert-outline</v-icon></span>
              </template>
              <span>Jangkaan disembunyikan: model regresi memberi nilai yang terlalu jauh dari harga sebenar (kemungkinan unit data tidak konsisten). Rekod masih ditanda melalui Z-Sisa / Mahalanobis.</span>
            </v-tooltip>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:item.residualZ="{ item }">
            <span :class="{ 'red--text font-weight-bold': item.regAnomaly }">{{ item.residualZ.toFixed(2) }}</span>
          </template>
          <template v-slot:item.mahalanobis="{ item }">
            <span :class="{ 'red--text font-weight-bold': item.mahalAnomaly }">{{ item.mahalanobis == null ? '—' : item.mahalanobis.toFixed(2) }}</span>
          </template>
          <template v-slot:item.methods="{ item }">
            <div class="d-flex" style="gap:2px">
              <span v-for="f in methodDots(item)" :key="f.code"
                class="method-dot" :class="f.on ? 'on' : 'off'" :title="f.code">{{ f.short }}</span>
            </div>
          </template>
          <template v-slot:item.upDirection="{ item }">
            <v-chip v-if="item.upDirection === 'Overpriced'" x-small color="red lighten-4" text-color="red">↑ Tinggi</v-chip>
            <v-chip v-else-if="item.upDirection === 'Underpriced'" x-small color="amber lighten-4" text-color="amber darken-3">↓ Rendah</v-chip>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:no-data>
            <div class="text-center grey--text pa-6">Tiada rekod dalam penapis</div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Method explainer -->
      <v-dialog v-model="methodDialog" max-width="720" scrollable>
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold blue-grey--text text--darken-3 py-3">
            <v-icon left color="indigo darken-2">mdi-function-variant</v-icon>
            Cara Pengiraan — Konsensus {{ methodCount }}-Kaedah
            <v-spacer></v-spacer>
            <v-btn icon small @click="methodDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="py-4 caption" style="line-height:1.7">
            <p class="mb-1 indigo--text text--darken-2"><b>Nota:</b> Semua kaedah menggunakan <b>RM Negeri</b> sebagai harga (bukan RM HQ).</p>
            <p><b>UP — Unit Price IQR.</b> Harga seunit = RM Negeri ÷ Kuantiti. Dikira Q1/Q3 atas
            keseluruhan rekod item; ditanda jika di luar pagar Tukey
            (Q1 − 1.5·IQR hingga Q3 + 1.5·IQR). Arah: melebihi pagar atas = <i>Tinggi</i>, di bawah pagar bawah = <i>Rendah</i>.</p>
            <p v-if="data.stats"><b>Pagar UP:</b> {{ fmtU(data.stats.upLower) }} hingga {{ fmtU(data.stats.upUpper) }}
            (Median {{ fmtU(data.stats.upMedian) }}).</p>
            <p><b>RG — Regression Residual.</b> Regresi log-log RM Negeri atas Kuantiti memberi harga
            jangkaan. Sisa (residual) ditukar ke skor-Z; ditanda jika |Z| &gt; 2σ.</p>
            <p><b>PG — Peer Group.</b> Sisihan harga seunit dari median item; ditanda jika lebih 50%.</p>
            <p><b>MD — Mahalanobis.</b> Jarak bivariat rekod dari pusat taburan (log Kuantiti, log RM Negeri)
            mengambil kira kovarians kedua-dua paksi. Ditanda jika jarak melebihi ambang
            khi-kuasa-dua 97.5% (≈ 2.72) — iaitu titik yang jauh dari corak harga↔kuantiti biasa.</p>
            <p class="mb-0"><b>Konsensus</b> = bilangan kaedah (0–{{ methodCount }}) yang menanda rekod.
            {{ methodCount }}/{{ methodCount }} = <b>Critical</b>, {{ methodCount - 1 }}/{{ methodCount }} = <b>High</b>,
            2/{{ methodCount }} = <b>Medium</b>, 1/{{ methodCount }} = <b>Low</b>, 0/{{ methodCount }} = <b>Normal</b>.</p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import Chart from "chart.js";
import { fetchConsensus, fetchConsensusRecords } from "./julatHargaData";

export default {
  name: "KonsensusKaedah",
  props: {
    kod: { type: String, default: null },
    negeri: { type: String, default: null },
    pusingan: { type: String, default: "" }, // already-built pusinganParam() string
    dp: { type: String, default: "" },       // CSV of selected Daerah Pentadbiran codes
    active: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    data: null,
    chart: null,
    methodDialog: false,
    // filters (drive the SERVER-paginated records table)
    confFilter: null,
    methodFilter: null,
    dirFilter: null,
    search: "",
    loadedKey: null, // guards against refetching identical kod/negeri/pusingan

    // ── Server-paginated records table ──
    tableItems: [],
    tableTotal: 0,
    tableLoading: false,
    tableOptions: { page: 1, itemsPerPage: 25, sortBy: ["consensus"], sortDesc: [true] },
    searchDebounce: null,
  }),
  computed: {
    // number of detection methods returned by the backend (drives /N scores + buckets)
    methodCount() {
      return (this.data && this.data.methods && this.data.methods.length) || 4;
    },
    anomalyTotal() {
      if (!this.data) return 0;
      // backend sends an exact full-dataset count; fall back to the bucket sum
      if (this.data.anomalyTotal != null) return this.data.anomalyTotal;
      const b = this.data.buckets || {};
      return (b.Critical || 0) + (b.High || 0) + (b.Medium || 0) + (b.Low || 0);
    },
    bucketCards() {
      const b = (this.data && this.data.buckets) || {};
      const n = this.methodCount;
      return [
        { key: "Critical", label: "Critical", value: b.Critical || 0, color: "#7f1d1d", desc: `${n}/${n} kaedah` },
        { key: "High", label: "High", value: b.High || 0, color: "#e11d48", desc: `${n - 1}/${n} kaedah` },
        { key: "Medium", label: "Medium", value: b.Medium || 0, color: "#f59e0b", desc: `2/${n} kaedah` },
        { key: "Low", label: "Low", value: b.Low || 0, color: "#7c3aed", desc: `1/${n} kaedah` },
        { key: "Normal", label: "Normal", value: b.Normal || 0, color: "#0EA5A4", desc: `0/${n} kaedah` },
      ];
    },
    confFilterItems() {
      return [
        { text: "Semua tahap", value: null },
        { text: "Critical sahaja", value: "Critical" },
        { text: "High sahaja", value: "High" },
        { text: "Medium sahaja", value: "Medium" },
        { text: "Low sahaja", value: "Low" },
        { text: "Normal sahaja", value: "Normal" },
        { text: "Semua anomali (kecuali Normal)", value: "__ANOM__" },
      ];
    },
    methodFilterItems() {
      return [
        { text: "Mana-mana kaedah", value: null },
        { text: "Unit Price IQR (UP)", value: "up" },
        { text: "Regression (RG)", value: "reg" },
        { text: "Peer Group (PG)", value: "peer" },
        { text: "Mahalanobis (MD)", value: "mahal" },
      ];
    },
    // Plain-language tooltips shown via the ⓘ icon in each special column header.
    helpHeaders() {
      return [
        { value: "rmNegeri", help: "Harga di peringkat negeri bagi sebutan ini. Asas untuk semua pengiraan. Bertukar MERAH bila harga seunit dikira ganjil (kaedah UP)." },
        { value: "unitPrice", help: "Harga untuk satu unit = RM Negeri ÷ Kuantiti. Membolehkan perbandingan adil walaupun kuantiti berbeza. MERAH bila di luar julat biasa." },
        { value: "expectedPrice", help: "Anggaran harga yang DIJANGKA untuk kuantiti ini, berdasarkan corak keseluruhan item (regresi). Bandingkan dengan RM Negeri sebenar untuk lihat sama ada terlalu mahal/murah." },
        { value: "residualZ", help: "Sejauh mana harga sebenar menyimpang dari jangkaan, dalam unit sisihan piawai. 0 = tepat ikut jangkaan. MERAH bila |nilai| > 2 (sangat jauh)." },
        { value: "mahalanobis", help: "Jarak titik ini dari kelompok purata (harga & kuantiti dipertimbang bersama). Makin besar makin terpencil. MERAH bila melebihi ambang ~2.72." },
        { value: "methods", help: "4 kotak mewakili 4 kaedah pengesanan (UP, RG, PG, MD). Kotak MERAH = kaedah itu menanda rekod ini sebagai ganjil; KELABU = tidak." },
        { value: "upDirection", help: "Arah anomali harga seunit: ↑ Tinggi (lebih mahal dari biasa) atau ↓ Rendah (lebih murah). '—' bermaksud harga dalam julat normal." },
      ];
    },
    // Legend rows describing each special column. Sample values + thresholds are
    // taken from THIS item's real data (stats + records), so the "merah bila …"
    // notes show the actual fences for the selected item, not placeholders.
    columnLegend() {
      const st = (this.data && this.data.stats) || {};
      const recs = (this.data && this.data.records) || [];
      // pick a real flagged example (most extreme) per column to use as sample
      const z = this.zScaleExample;
      const md = this.mahalScaleExample;
      // a flagged UP record (for RM Negeri / RM/Unit samples), else any record
      const upEx = recs.find((r) => r.upAnomaly) || recs[0] || null;
      const anyExpected = recs.find((r) => r.expectedPrice != null) || null;

      const red = (txt) => `<span class="red--text font-weight-bold">${txt}</span>`;
      const grey = (txt) => `<span class="grey--text">${txt}</span>`;

      const hasUpFence = st.upLower != null && st.upUpper != null;
      const mdThresh = st.mahalChi2 != null ? Math.sqrt(st.mahalChi2) : null;

      return [
        {
          key: "rmNegeri", name: "RM Negeri",
          sample: upEx ? red(this.fmt(upEx.rmNegeri)) : grey("—"),
          desc: "Harga peringkat negeri — asas semua pengiraan.",
          on: "Merah & tebal bila harga seunit ganjil (UP).",
        },
        {
          key: "unitPrice", name: "RM/Unit",
          sample: upEx && upEx.unitPrice != null ? red(this.fmtU(upEx.unitPrice)) : grey("—"),
          desc: "RM Negeri ÷ Kuantiti — harga satu unit untuk perbandingan adil.",
          on: hasUpFence
            ? `Merah bila < ${this.fmtU(st.upLower)} atau > ${this.fmtU(st.upUpper)} (median ${this.fmtU(st.upMedian)}).`
            : "Merah bila di luar julat biasa (UP).",
        },
        {
          key: "expectedPrice", name: "Jangkaan",
          sample: anyExpected ? grey(this.fmt(anyExpected.expectedPrice)) : grey("—"),
          desc: "Harga yang dijangka untuk kuantiti ini (model regresi). Bandingkan dengan RM Negeri sebenar.",
          on: "",
        },
        {
          key: "residualZ", name: "Z-Sisa",
          sample: z.has ? red(z.markerZ.toFixed(2)) : grey("—"),
          desc: "Berapa jauh harga menyimpang dari jangkaan (unit sisihan piawai). Hampir 0 = normal.",
          on: "Merah bila < −2 atau > +2 (terlalu jauh dari jangkaan).",
        },
        {
          key: "mahalanobis", name: "Mahalanobis",
          sample: md.has ? red(md.markerD.toFixed(2)) : grey("—"),
          desc: "Jarak rekod dari kelompok purata (harga & kuantiti bersama). Besar = terpencil.",
          on: mdThresh != null
            ? `Merah bila > ${mdThresh.toFixed(2)} (ambang item ini).`
            : "Merah bila melebihi ambang.",
        },
        {
          key: "upDirection", name: "Arah",
          sample: '<span class="red--text">↑ Tinggi</span> / <span class="amber--text text--darken-3">↓ Rendah</span>',
          desc: "Arah anomali harga: lebih mahal (↑) atau lebih murah (↓) dari biasa.",
          on: "↑ merah = mahal · ↓ kuning = murah · — = normal.",
        },
      ];
    },
    // Key for the 4 method dots in the "Kaedah" column.
    methodKey() {
      return [
        { code: "UP", short: "U", name: "Unit Price IQR", desc: "Harga seunit di luar julat Tukey (1.5×IQR)." },
        { code: "RG", short: "R", name: "Regression", desc: "Harga jauh dari jangkaan model (|Z| > 2)." },
        { code: "PG", short: "P", name: "Peer Group", desc: "Harga seunit menyimpang > 50% dari median item." },
        { code: "MD", short: "M", name: "Mahalanobis", desc: "Titik terpencil pada graf harga↔kuantiti." },
      ];
    },
    // ── Worked examples driven by THIS item's real records ───────────────────
    // Pick a representative flagged record (the most extreme) so the legend shows
    // why a real row in this very table turned red — not an abstract example.
    zScaleExample() {
      const recs = (this.data && this.data.records) || [];
      const THRESH = 2;
      const SPAN = 4; // number line runs −SPAN..+SPAN
      // most extreme |z| in this item
      let ex = null;
      recs.forEach((r) => {
        if (r.residualZ == null) return;
        if (!ex || Math.abs(r.residualZ) > Math.abs(ex.residualZ)) ex = r;
      });
      const z = ex ? ex.residualZ : 0;
      const clamp = (v) => Math.max(-SPAN, Math.min(SPAN, v));
      const pct = (v) => ((clamp(v) + SPAN) / (2 * SPAN)) * 100; // 0..100
      return {
        thresh: THRESH, span: SPAN,
        markerZ: z,
        markerPct: pct(z),
        lowerPct: pct(-THRESH),
        upperPct: pct(THRESH),
        flagged: ex ? ex.regAnomaly : false,
        hid: ex ? ex.hid : "",
        negeri: ex ? (ex.negeriName || ex.negeri) : "",
        rmNegeri: ex ? ex.rmNegeri : null,
        expected: ex ? ex.expectedPrice : null, // may be null if suppressed
        // the example is about Z-Sisa, so it's valid as long as a Z exists —
        // even when the (implausible) expected price was hidden.
        has: !!ex,
      };
    },
    mahalScaleExample() {
      const recs = (this.data && this.data.records) || [];
      const thresh = (this.data && this.data.stats && this.data.stats.mahalChi2)
        ? Math.sqrt(this.data.stats.mahalChi2) : 2.716; // distance threshold ≈ √7.378
      let ex = null, maxD = 0;
      recs.forEach((r) => {
        if (r.mahalanobis == null) return;
        if (!ex || r.mahalanobis > ex.mahalanobis) ex = r;
        if (r.mahalanobis > maxD) maxD = r.mahalanobis;
      });
      const d = ex ? ex.mahalanobis : 0;
      // scale runs 0..max(threshold*1.6, observed max) so both fit
      const span = Math.max(thresh * 1.6, maxD * 1.05, thresh + 0.5);
      const pct = (v) => Math.max(0, Math.min(100, (v / span) * 100));
      return {
        thresh, span,
        markerD: d,
        markerPct: pct(d),
        threshPct: pct(thresh),
        flagged: ex ? ex.mahalAnomaly : false,
        hid: ex ? ex.hid : "",
        negeri: ex ? (ex.negeriName || ex.negeri) : "",
        rmNegeri: ex ? ex.rmNegeri : null,
        kuantiti: ex ? ex.kuantiti : null,
        has: !!ex,
      };
    },
    dirFilterItems() {
      return [
        { text: "Semua arah", value: null },
        { text: "Tinggi (Overpriced)", value: "Overpriced" },
        { text: "Rendah (Underpriced)", value: "Underpriced" },
      ];
    },
    headers() {
      return [
        { text: "Tahap", value: "confidence", width: 100 },
        { text: "Skor", value: "consensus", align: "end", width: 70 },
        { text: "HID", value: "hid", width: 90 },
        // Negeri/Pusingan are enriched from MySQL after the ClickHouse query, so
        // the server can't sort by them — disable their sort arrows.
        { text: "Negeri", value: "negeriName", width: 130, sortable: false },
        { text: "Pusingan", value: "pusingan", align: "end", width: 90, sortable: false },
        { text: "RM Negeri", value: "rmNegeri", align: "end", width: 110 },
        { text: "RM HQ", value: "rmHQ", align: "end", width: 100 },
        { text: "Kuantiti", value: "kuantiti", align: "end", width: 100 },
        { text: "RM/Unit", value: "unitPrice", align: "end", width: 110 },
        { text: "Jangkaan", value: "expectedPrice", align: "end", width: 110 },
        { text: "Z-Sisa", value: "residualZ", align: "end", width: 90 },
        { text: "Mahalanobis", value: "mahalanobis", align: "end", width: 110 },
        { text: "Kaedah", value: "methods", sortable: false, width: 100 },
        { text: "Arah", value: "upDirection", width: 100 },
      ];
    },
    exportName() {
      return `konsensus-${this.methodCount}kaedah_${this.kod || "item"}.csv`;
    },
    // The scatter plots RM Negeri vs Kuantiti — RM Negeri is the price every
    // method is computed on, so the chart matches the analysis.
    yLabel() {
      return "RM Negeri";
    },
    scatterPoints() {
      const recs = (this.data && this.data.records) || [];
      const key = "rmNegeri";
      // log scales need strictly-positive values on both axes
      return recs
        .filter((r) => r.kuantiti > 0 && r[key] > 0)
        .map((r) => ({ x: r.kuantiti, y: r[key], rec: r }));
    },
  },
  watch: {
    kod() { this.maybeLoad(); },
    negeri() { this.maybeLoad(); },
    pusingan() { this.maybeLoad(); },
    dp() { this.maybeLoad(); },
    active(v) {
      if (!v) return;
      this.maybeLoad();
      // if data is already loaded (tab re-opened), redraw — the canvas may have
      // been sized 0×0 while the tab was hidden, leaving an empty chart.
      if (this.data) this.scheduleDraw();
    },
    // page / itemsPerPage / sort change → refetch the records page from the server
    tableOptions: {
      handler() { if (this.active && this.kod) this.fetchTable(); },
      deep: true,
    },
  },
  mounted() {
    if (this.active) this.maybeLoad();
  },
  methods: {
    // Only fetch when the tab is active AND inputs changed — avoids a heavy
    // 500k-row consensus run while the user is still on the IQR tab.
    maybeLoad() {
      if (!this.active || !this.kod) return;
      const key = `${this.kod}|${this.negeri || ""}|${this.pusingan || ""}|${this.dp || ""}`;
      if (key === this.loadedKey) return;
      this.loadedKey = key;
      this.load();
    },
    async load() {
      this.loading = true;
      this.data = null;
      this.destroyChart();
      // reset the records table to page 1 for the newly-selected item
      this.confFilter = null;
      this.methodFilter = null;
      this.dirFilter = null;
      this.search = "";
      this.tableItems = [];
      this.tableTotal = 0;
      this.tableOptions = { page: 1, itemsPerPage: 25, sortBy: ["consensus"], sortDesc: [true] };
      try {
        this.data = await fetchConsensus({
          kod: this.kod, negeri: this.negeri, pusingan: this.pusingan, dp: this.dp,
        });
        // loading flips to false below; wait a frame so the v-else-if="data"
        // block (with the canvas) is in the DOM AND laid out before drawing.
        this.scheduleDraw();
        // the records table fetches via the tableOptions watcher (reset above);
        // ensure a load even if the options object happened to be deep-equal.
        this.$nextTick(() => { if (!this.tableLoading && !this.tableItems.length) this.fetchTable(); });
      } catch (e) {
        console.error("fetchConsensus failed:", e);
        this.$emit("error", "Gagal mengira konsensus anomali");
      }
      this.loading = false;
    },
    // common request params built from the active filters/sort/pagination
    tableParams(extra = {}) {
      const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
      return {
        kod: this.kod, negeri: this.negeri, pusingan: this.pusingan, dp: this.dp,
        page, itemsPerPage,
        sortBy: (sortBy && sortBy.length) ? sortBy[0] : "consensus",
        sortDesc: (sortDesc && sortDesc.length) ? sortDesc[0] : true,
        conf: this.confFilter || "",
        method: this.methodFilter || "",
        direction: this.dirFilter || "",
        search: this.search || "",
        ...extra,
      };
    },
    async fetchTable() {
      if (!this.kod) return;
      this.tableLoading = true;
      try {
        const res = await fetchConsensusRecords(this.tableParams());
        this.tableItems = res.items || [];
        this.tableTotal = res.total || 0;
      } catch (e) {
        console.error("fetchConsensusRecords failed:", e);
        this.$emit("error", "Gagal memuatkan rekod konsensus");
      }
      this.tableLoading = false;
    },
    // a filter changed → back to page 1 and refetch
    onFilterChange() {
      if (this.tableOptions.page !== 1) {
        this.tableOptions.page = 1; // triggers the tableOptions watcher → fetchTable
      } else {
        this.fetchTable();
      }
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.onFilterChange(), 350);
    },
    // CSV export: pull ALL matching rows (itemsPerPage = -1) for the current filters
    async exportFetch() {
      const res = await fetchConsensusRecords(this.tableParams({ itemsPerPage: -1, page: 1 }));
      return (res.items || []).map((r) => ({
        HID: r.hid,
        Negeri: r.negeriName,
        Pusingan: r.pusingan,
        "RM Negeri": r.rmNegeri,
        "RM HQ": r.rmHQ,
        Kuantiti: r.kuantiti,
        "RM/Unit": r.unitPrice == null ? "" : r.unitPrice,
        Jangkaan: r.expectedPrice == null ? "" : Number(r.expectedPrice).toFixed(4),
        "Z-Sisa": Number(r.residualZ).toFixed(4),
        Mahalanobis: r.mahalanobis == null ? "" : Number(r.mahalanobis).toFixed(4),
        UP: r.upAnomaly, RG: r.regAnomaly, PG: r.peerAnomaly, MD: r.mahalAnomaly,
        Konsensus: r.consensus, Tahap: r.confidence, Arah: r.upDirection,
      }));
    },
    // chip background (a light tint) + readable dark text per confidence level.
    // Critical/High are filled solid (white text) so they stand out.
    confBg(c) {
      return c === "Critical" ? "red darken-4"
        : c === "High" ? "red lighten-4"
        : c === "Medium" ? "amber lighten-4"
        : c === "Low" ? "deep-purple lighten-4"
        : "teal lighten-4";
    },
    confText(c) {
      return c === "Critical" ? "white"
        : c === "High" ? "red darken-2"
        : c === "Medium" ? "amber darken-4"
        : c === "Low" ? "deep-purple darken-2"
        : "teal darken-3";
    },
    pointColor(c) {
      return c === "Critical" ? "#7f1d1d"
        : c === "High" ? "#e11d48"
        : c === "Medium" ? "#f59e0b"
        : c === "Low" ? "#7c3aed"
        : "#0EA5A4";
    },
    methodDots(r) {
      return [
        { code: "UP", short: "U", on: r.upAnomaly },
        { code: "RG", short: "R", on: r.regAnomaly },
        { code: "PG", short: "P", on: r.peerAnomaly },
        { code: "MD", short: "M", on: r.mahalAnomaly },
      ];
    },
    toggleConf(key) {
      this.confFilter = this.confFilter === key ? null : key;
    },
    fmt(n) {
      if (n == null || isNaN(n)) return "RM 0.00";
      return "RM " + Number(n).toLocaleString("ms-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtU(n) {
      if (n == null || isNaN(n)) return "—";
      return "RM " + Number(n).toLocaleString("ms-MY", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    },
    fmtN(n) {
      if (n == null || isNaN(n)) return "0";
      return Number(n).toLocaleString();
    },
    destroyChart() {
      if (this.chart) { this.chart.destroy(); this.chart = null; }
    },
    // Draw once the canvas is actually laid out. Inside a v-tab-item / freshly
    // toggled v-else-if block the canvas can briefly have 0×0 size, which makes
    // Chart.js render an invisible (empty) chart. Poll a few frames for a real
    // size before drawing; give up after ~1s and draw anyway.
    scheduleDraw(tries = 0) {
      this.$nextTick(() => {
        const canvas = this.$refs.scatter;
        const ready = canvas && canvas.clientWidth > 0 && canvas.clientHeight > 0;
        if (ready || tries > 30) {
          this.drawScatter();
        } else {
          // canvas not sized yet (tab still animating / not painted) — retry
          window.requestAnimationFrame(() => this.scheduleDraw(tries + 1));
        }
      });
    },
    drawScatter() {
      const canvas = this.$refs.scatter;
      if (!canvas || !this.data) return;
      const pts = this.scatterPoints;
      this.destroyChart();
      if (!pts.length) return;
      // Chart.js v2: scriptable colour/radius funcs are unreliable, so precompute
      // parallel arrays (one entry per point) keyed by confidence.
      const bg = pts.map((p) => this.pointColor(p.rec.confidence) + "cc");
      const border = pts.map((p) => this.pointColor(p.rec.confidence));
      const radius = pts.map((p) => {
        const k = p.rec.confidence;
        return k === "Critical" ? 8 : k === "High" ? 6 : k === "Medium" ? 5 : 3;
      });
      const ctx = canvas.getContext("2d");
      const self = this;
      this.chart = new Chart(ctx, {
        type: "scatter",
        data: {
          datasets: [{
            label: "Rekod",
            data: pts,
            backgroundColor: bg,
            borderColor: border,
            borderWidth: 1,
            pointRadius: radius,
            pointHoverRadius: 10,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            callbacks: {
              label: (item, d) => {
                const p = d.datasets[item.datasetIndex].data[item.index];
                const r = p && p.rec;
                if (!r) return "";
                return [
                  `${r.negeriName || r.negeri} · Pusingan ${r.pusingan}`,
                  `${self.yLabel} ${self.fmt(p.y)} / ${self.fmtN(r.kuantiti)} unit`,
                  `RM/unit: ${self.fmtU(r.unitPrice)}`,
                  `Konsensus: ${r.consensus}/${self.methodCount} (${r.confidence})`,
                ];
              },
            },
          },
          scales: {
            xAxes: [{
              type: "logarithmic",
              scaleLabel: { display: true, labelString: "Kuantiti (log)" },
              ticks: {
                // v2 logarithmic axis auto-emits many minor ticks; only label the
                // "nice" 1/2/5×10ⁿ ones and blank the rest (return '' not null).
                callback: (v) => {
                  if (v == null || v <= 0) return "";
                  const p = Math.pow(10, Math.floor(Math.log10(v)));
                  const m = v / p;
                  return (m === 1 || m === 2 || m === 5) ? Number(v).toLocaleString() : "";
                },
              },
            }],
            yAxes: [{
              type: "logarithmic",
              scaleLabel: { display: true, labelString: "RM HQ (log)" },
              ticks: {
                callback: (v) => {
                  if (v == null || v <= 0) return "";
                  const p = Math.pow(10, Math.floor(Math.log10(v)));
                  const m = v / p;
                  return (m === 1 || m === 2 || m === 5) ? "RM " + Number(v).toLocaleString() : "";
                },
              },
            }],
          },
        },
      });
      // force a layout pass so the canvas picks up its container size even if it
      // was first created while the tab was still settling.
      this.chart.resize();
    },
  },
  beforeDestroy() {
    this.destroyChart();
    clearTimeout(this.searchDebounce);
  },
};
</script>

<style scoped>
.chart-container { position: relative; height: 420px; width: 100%; }

.consensus-head {
  border-left: 5px solid #3949ab !important;
  background: linear-gradient(90deg, #E8EAF6 0%, #F5F6FC 45%, #FFFFFF 100%) !important;
}

.conf-card { cursor: pointer; transition: box-shadow .15s ease, transform .15s ease; }
.conf-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,.06); }
.conf-card.active { box-shadow: 0 4px 16px rgba(57,73,171,.18); border-color: #3949ab !important; }

.method-card {
  padding: 12px 14px;
  background: #fafbfc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  height: 100%;
}
.method-code {
  width: 40px; height: 40px; border-radius: 9px;
  background: #1a237e; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: var(--fs-xs); flex-shrink: 0;
}

.method-dot {
  width: 18px; height: 18px; border-radius: 4px;
  font-size: var(--fs-3xs); font-weight: 700; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
}
.method-dot.on { background: #e11d48; }
.method-dot.off { background: #e2e8f0; color: #94a3b8; }

/* ── Legend / panduan jadual ───────────────────────────────────────────── */
.legend-panel { border: 1px solid #e2e8f0; border-radius: 8px; margin: 0 16px 8px; }
.legend-panel >>> .v-expansion-panel-header { min-height: 40px; }
.legend-help-icon { color: #b0bec5; cursor: help; }
.legend-help-icon:hover { color: #5c6bc0; }

.legend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.legend-item {
  background: #fafbfc;
  border: 1px solid #eceff1;
  border-radius: 8px;
  padding: 8px 10px;
}
.legend-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.legend-name { font-size: var(--fs-xs); font-weight: 700; color: #37474f; }
.legend-sample { font-size: var(--fs-xs); font-family: 'JetBrains Mono', monospace; white-space: nowrap; }
.legend-desc { font-size: var(--fs-xs); color: #607d8b; line-height: 1.45; margin-top: 3px; }
.legend-on {
  display: flex; align-items: center; gap: 4px;
  font-size: var(--fs-3xs); color: #c62828; margin-top: 5px; font-weight: 500;
}

.dot-key { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 8px 14px; }
.dot-key-item { display: flex; align-items: flex-start; gap: 8px; }
.dk-name { font-size: var(--fs-xs); font-weight: 700; color: #37474f; }
.dk-desc { font-size: var(--fs-xs); color: #607d8b; line-height: 1.4; }

.conf-key { display: flex; flex-wrap: wrap; }

/* ── Worked example number lines ───────────────────────────────────────── */
.example-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 14px;
}
.example-box {
  background: #fafbfc;
  border: 1px solid #eceff1;
  border-radius: 8px;
  padding: 10px 12px 14px;
}
.ex-title { font-size: var(--fs-xs); font-weight: 700; color: #37474f; }
.ex-sub { font-size: var(--fs-3xs); color: #78909c; line-height: 1.4; margin: 2px 0 14px; }

.numline {
  position: relative;
  height: 8px;
  background: #e3f2e8;            /* green-ish "normal" band */
  border-radius: 4px;
  margin: 18px 6px 22px;
}
.nl-zone {
  position: absolute; top: 0; bottom: 0;
  background: repeating-linear-gradient(45deg, #ffcdd2, #ffcdd2 5px, #ef9a9a 5px, #ef9a9a 10px);
  opacity: 0.9;
}
.nl-zone:first-of-type { border-radius: 4px 0 0 4px; }
.nl-tick {
  position: absolute; top: -4px; bottom: -4px;
  width: 0; border-left: 1px dashed #90a4ae;
  transform: translateX(-0.5px);
}
.nl-tick.center { left: 50%; }
.nl-tick span {
  position: absolute; bottom: -16px; left: 50%;
  transform: translateX(-50%);
  font-size: var(--fs-3xs); color: #607d8b; font-family: 'JetBrains Mono', monospace; white-space: nowrap;
}
.nl-marker {
  position: absolute; top: 50%;
  width: 13px; height: 13px; border-radius: 50%;
  background: #0EA5A4; border: 2px solid #fff;
  box-shadow: 0 0 0 1px #0a7c7b;
  transform: translate(-50%, -50%);
  z-index: 2;
}
.nl-marker.flagged {
  background: #e11d48; box-shadow: 0 0 0 1px #9f1239, 0 0 6px rgba(225,29,72,.5);
}
.ex-read { font-size: var(--fs-3xs); color: #546e7a; line-height: 1.5; }
</style>
